"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// @owlcat/app-shell — the fleet's shared support entry (replaces Tawk.to live chat).
//
// A small, theme-aware, accessible contact form mounted once per app. It renders
// a floating trigger button (bottom-right, like the old chat bubble) that opens a
// dialog with Subject + Message + a reply-contact section. On open it does a
// credentialed GET `${accountsUrl}/me` to learn the logged-in submitter's known
// contact and offer a reply on it: their email (prefilled, editable) OR — when no
// email but a Telegram login is linked — a Telegram @handle field. Sharing that
// contact requires an EXPLICIT consent checkbox ("I'd like a reply…") — unchecked
// means an anonymous submission (no contact leaves the browser). Not logged in /
// no known contact falls back to a plain optional email input. On submit it POSTs
// `${accountsUrl}/support` (absolute, credentialed so the owlcat-id cookie is
// captured server-side; anonymous submissions allowed). accounts persists the
// request (incl. the consented contact) and fires a best-effort Telegram alert.
//
// `accountsUrl` is the app's NEXT_PUBLIC_ACCOUNTS_URL (a NEXT_PUBLIC var must be
// referenced literally in the app to be inlined, so it is a prop, mirroring
// LegalFooter). Any chrome can open the dialog without rendering its own trigger
// via openSupportWidget() (dispatches SUPPORT_OPEN_EVENT) — e.g. a sidebar
// "Support" link — so the floating button and a nav entry share one dialog.
//
// Contrast/colour come entirely from the @owlcat/ui + @owlcat/theme DS tokens
// (correct in light + dark); no bespoke colours. A hidden honeypot field
// (`company`) blunts bot spam — accounts drops any submission that fills it.
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Input, Label, Textarea, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, } from "@owlcat/ui";
import { useTrack } from "./analytics";
/** Window event any app chrome can dispatch to open the shared support dialog. */
export const SUPPORT_OPEN_EVENT = "owlcat:open-support";
/** Open the shared <SupportWidget> dialog from anywhere (e.g. a sidebar link). */
export function openSupportWidget() {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SUPPORT_OPEN_EVENT));
    }
}
const DEFAULT_LABELS = {
    trigger: "Support",
    title: "Contact support",
    description: "Send us a message and we'll get back to you.",
    subject: "Subject",
    message: "Message",
    email: "Email (optional)",
    emailHint: "Add your email if you'd like a reply.",
    send: "Send",
    sending: "Sending…",
    cancel: "Cancel",
    success: "Thanks! Your message has been sent.",
    error: "Couldn't send your message. Please try again.",
    consent: "I'd like a reply — share my contact so support can respond.",
    emailReply: "Reply email",
    telegram: "Telegram",
    telegramPlaceholder: "@username",
    telegramHint: "We'll reply to you on Telegram.",
};
export function SupportWidget({ accountsUrl, appId, labels, floating = true, }) {
    const t = { ...DEFAULT_LABELS, ...labels };
    const base = (accountsUrl ?? "").trim().replace(/\/+$/, "");
    const endpoint = `${base}/support`;
    const [open, setOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("");
    const [telegram, setTelegram] = useState("");
    const [company, setCompany] = useState(""); // honeypot — humans never see this
    const [state, setState] = useState("idle");
    // Reply-contact derived from /me. Default anonymous (plain optional email); the
    // consent box defaults CHECKED only once we learn we have a known contact.
    const [contactMode, setContactMode] = useState("anonymous");
    const [consent, setConsent] = useState(false);
    // Fleet analytics (consent-gated, prod-only inside): support_opened {surface} on
    // every open (floating bubble vs. app chrome via openSupportWidget), support_sent
    // once a request is accepted. No free text ever leaves as a property.
    const track = useTrack();
    const openedVia = useRef("floating");
    useEffect(() => {
        if (open)
            track("support_opened", { surface: openedVia.current });
    }, [open, track]);
    // Let any chrome (sidebar link, header) open the dialog without its own trigger.
    useEffect(() => {
        const openIt = () => {
            openedVia.current = "chrome";
            setOpen(true);
        };
        window.addEventListener(SUPPORT_OPEN_EVENT, openIt);
        return () => window.removeEventListener(SUPPORT_OPEN_EVENT, openIt);
    }, []);
    // On open, fetch the logged-in submitter's known contact (credentialed, CORS).
    // 401/network → stay anonymous (plain optional email). We only ever prefill the
    // OWNER's own data; sharing it still requires the explicit consent box.
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${base}/me`, {
                    method: "GET",
                    credentials: "include",
                    headers: { accept: "application/json" },
                });
                if (!res.ok)
                    return; // anonymous / not logged in
                const me = (await res.json());
                const c = me?.contact;
                if (cancelled || !c)
                    return;
                if (c.email) {
                    setContactMode("email");
                    setEmail(c.email);
                    setConsent(true);
                }
                else if (c.telegramLinked) {
                    setContactMode("telegram");
                    setTelegram(c.telegram ?? "");
                    setConsent(true);
                }
            }
            catch {
                /* stay anonymous */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, base]);
    // Reset transient state when the dialog closes.
    const onOpenChange = useCallback((next) => {
        setOpen(next);
        if (!next)
            setState("idle");
    }, []);
    const canSend = subject.trim().length > 0 && message.trim().length > 0 && state !== "sending";
    const submit = useCallback(async (e) => {
        e.preventDefault();
        if (!canSend)
            return;
        setState("sending");
        // Contact is shared ONLY with explicit consent. In every mode the box (or, for
        // anonymous, a typed email) gates whether any contact leaves the browser.
        const shareEmail = contactMode === "anonymous"
            ? email.trim() || undefined
            : contactMode === "email" && consent
                ? email.trim() || undefined
                : undefined;
        const shareTelegram = contactMode === "telegram" && consent ? telegram.trim() || undefined : undefined;
        const contactConsent = Boolean(shareEmail || shareTelegram);
        try {
            const res = await fetch(endpoint, {
                method: "POST",
                credentials: "include", // send the owlcat-id cookie cross-subdomain
                headers: { "content-type": "application/json" },
                body: JSON.stringify({
                    subject: subject.trim(),
                    message: message.trim(),
                    email: shareEmail,
                    telegram: shareTelegram,
                    contactConsent,
                    appId,
                    url: typeof window !== "undefined" ? window.location.href : undefined,
                    company, // honeypot
                }),
            });
            if (!res.ok)
                throw new Error(`HTTP ${res.status}`);
            setState("ok");
            track("support_sent", {});
            setSubject("");
            setMessage("");
            setEmail("");
            setTelegram("");
        }
        catch {
            setState("error");
        }
    }, [canSend, endpoint, subject, message, email, telegram, consent, contactMode, appId, company, track]);
    return (_jsxs(_Fragment, { children: [floating && (_jsx(Button, { type: "button", onClick: () => {
                    openedVia.current = "floating";
                    setOpen(true);
                }, "data-testid": "support-trigger", "aria-label": t.trigger, className: "fixed bottom-4 right-4 z-40 shadow-lg", children: t.trigger })), _jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { className: "sm:max-w-md", "data-testid": "support-dialog", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: t.title }), _jsx(DialogDescription, { children: t.description })] }), state === "ok" ? (_jsx("p", { className: "py-4 text-sm text-muted-foreground", "data-testid": "support-success", children: t.success })) : (_jsxs("form", { onSubmit: submit, className: "flex flex-col gap-4", children: [_jsx("div", { "aria-hidden": true, className: "hidden", children: _jsxs("label", { children: ["Company", _jsx("input", { type: "text", tabIndex: -1, autoComplete: "off", value: company, onChange: (e) => setCompany(e.target.value) })] }) }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "support-subject", children: t.subject }), _jsx(Input, { id: "support-subject", "data-testid": "support-subject", value: subject, onChange: (e) => setSubject(e.target.value), maxLength: 200, required: true })] }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "support-message", children: t.message }), _jsx(Textarea, { id: "support-message", "data-testid": "support-message", value: message, onChange: (e) => setMessage(e.target.value), rows: 5, maxLength: 5000, required: true })] }), contactMode === "anonymous" && (_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "support-email", children: t.email }), _jsx(Input, { id: "support-email", "data-testid": "support-email", type: "email", value: email, onChange: (e) => setEmail(e.target.value), maxLength: 320, "aria-describedby": "support-email-hint" }), _jsx("span", { id: "support-email-hint", className: "text-xs text-muted-foreground", children: t.emailHint })] })), contactMode !== "anonymous" && (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsxs("div", { className: "flex items-start gap-2", children: [_jsx(Checkbox, { id: "support-consent", "data-testid": "support-consent", checked: consent, onCheckedChange: (v) => setConsent(v === true), className: "mt-0.5" }), _jsx(Label, { htmlFor: "support-consent", className: "text-sm font-normal leading-snug", children: t.consent })] }), consent && contactMode === "email" && (_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "support-email", children: t.emailReply }), _jsx(Input, { id: "support-email", "data-testid": "support-email", type: "email", value: email, onChange: (e) => setEmail(e.target.value), maxLength: 320 })] })), consent && contactMode === "telegram" && (_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "support-telegram", children: t.telegram }), _jsx(Input, { id: "support-telegram", "data-testid": "support-telegram", value: telegram, onChange: (e) => setTelegram(e.target.value), placeholder: t.telegramPlaceholder, maxLength: 64, "aria-describedby": "support-telegram-hint" }), _jsx("span", { id: "support-telegram-hint", className: "text-xs text-muted-foreground", children: t.telegramHint })] }))] })), state === "error" && (_jsx("p", { className: "text-sm text-destructive", "data-testid": "support-error", role: "alert", children: t.error })), _jsxs(DialogFooter, { children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => onOpenChange(false), children: t.cancel }), _jsx(Button, { type: "submit", disabled: !canSend, "data-testid": "support-send", children: state === "sending" ? t.sending : t.send })] })] }))] }) })] }));
}
